import { getTodayOperatingHours } from '@/lib/operatingHours';
import { getKoreanDate } from '@/lib/dateUtils';
import type { Restaurant } from '@/services/restaurant';
import type { Product, OrderType } from '@/pages/store/store-detail.types';

interface PickupTimeNoticeProps {
  restaurant: Restaurant;
  products: Product[];
  selectedOrderType: OrderType;
}

const toHHMM = (time?: string | null) => {
  if (!time) return undefined;
  const [h, m] = time.split(':');
  if (h === undefined || m === undefined) return undefined;
  return `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
};

const toMinutes = (time: string) => {
  const [h, m] = time.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return Number.NaN;
  return h * 60 + m;
};

const toTimestamp = (value?: string | null) => {
  if (!value) return null;
  const d = new Date(value);
  const ms = d.getTime();
  return Number.isNaN(ms) ? null : ms;
};

const isOfferActiveToday = (offer: NonNullable<Product['allOffers']>[number], today: string, now: Date) => {
  if (offer.offer_type !== 'DEADLINE_DISCOUNT' || offer.is_active !== true) return false;

  if (offer.valid_from) {
    const offerDate = offer.valid_from.includes('T') ? offer.valid_from.split('T')[0] : offer.valid_from;
    if (offerDate !== today) return false;
  }

  if (offer.valid_until) {
    const validUntil = new Date(offer.valid_until);
    if (!Number.isNaN(validUntil.getTime()) && now > validUntil) return false;
  }

  return true;
};

const buildDeadlinePickupText = (products: Product[], restaurant: Restaurant) => {
  const today = getKoreanDate();
  const now = new Date();

  const allDeadlineOffers = products.flatMap((product) => {
    const offers = product.allOffers || [];
    return offers.filter((offer) => offer.offer_type === 'DEADLINE_DISCOUNT');
  });

  const activeOffers = allDeadlineOffers.filter((offer) => isOfferActiveToday(offer, today, now));

  // 1) 활성 오퍼의 픽업 시간으로 대표 구간 계산
  const pickupWindows = activeOffers
    .map((offer) => ({
      start: toHHMM(offer.deadline_pickup_start_time || null),
      end: toHHMM(offer.deadline_pickup_end_time || null)
    }))
    .filter((win) => win.start || win.end);

  if (pickupWindows.length > 0) {
    const starts = pickupWindows.map((w) => w.start).filter(Boolean) as string[];
    const ends = pickupWindows.map((w) => w.end).filter(Boolean) as string[];

    const earliestStart = starts.length
      ? starts.reduce((min, cur) => (toMinutes(cur) < toMinutes(min) ? cur : min), starts[0])
      : undefined;
    const latestEnd = ends.length
      ? ends.reduce((max, cur) => (toMinutes(cur) > toMinutes(max) ? cur : max), ends[0])
      : undefined;

    if (earliestStart && latestEnd) return `${earliestStart} ~ ${latestEnd}`;
    if (earliestStart) return earliestStart;
    if (latestEnd) return latestEnd;
  }

  // 2) 활성 오퍼는 있으나 시간 정보가 없을 때: 매장 안내문 사용
  if (activeOffers.length > 0) {
    return restaurant.pickup_instructions || '수령가능시간 확인';
  }

  // 3) 비활성 오퍼라도 가장 최근 오퍼의 수령시간 사용
  if (allDeadlineOffers.length > 0) {
    const sortedByRecency = [...allDeadlineOffers].sort((a, b) => {
      const aTime = toTimestamp(a.valid_from) ?? toTimestamp(a.valid_until) ?? -Infinity;
      const bTime = toTimestamp(b.valid_from) ?? toTimestamp(b.valid_until) ?? -Infinity;
      return bTime - aTime;
    });

    const recentOfferWithTime = sortedByRecency.find(
      (offer) => offer.deadline_pickup_start_time || offer.deadline_pickup_end_time
    ) || sortedByRecency[0];

    const start = toHHMM(recentOfferWithTime?.deadline_pickup_start_time || null);
    const end = toHHMM(recentOfferWithTime?.deadline_pickup_end_time || null);

    if (start && end) return `${start} ~ ${end}`;
    if (start) return start;
    if (end) return end;

    return restaurant.pickup_instructions || '수령가능시간 확인';
  }

  // 4) 오퍼가 전혀 없을 때: 영업시간으로 안내(쉬는 날이어도 그대로 노출)
  const operatingHours = getTodayOperatingHours(restaurant as any);
  if (operatingHours.openTime && operatingHours.closeTime) {
    return `${operatingHours.openTime} ~ ${operatingHours.closeTime}`;
  }

  return restaurant.pickup_instructions || '수령가능시간 확인';
};

export function PickupTimeNotice({ restaurant, products, selectedOrderType }: PickupTimeNoticeProps) {
  if (selectedOrderType !== 'DEADLINE_DISCOUNT') return null;

  const pickupText = buildDeadlinePickupText(products, restaurant);

  return (
    <div className="px-4 mb-4">
      <div className="rounded-xl border-2 border-[#62C56E] bg-[#F0FFF2] px-4 py-3 text-[17px] md:text-[19px] text-[#0A3D0A] font-semibold text-center shadow-[0_4px_12px_rgba(10,61,10,0.08)]">
        수령가능시간 {pickupText}
      </div>
    </div>
  );
}
